'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PercentOffCalculator() {
  const t = useTranslations("tools.percentOffCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    originalPrice: '',
    percentOff: '',
    hasAdditional: false,
    additionalPercent: ''
  });

  const [results, setResults] = useState<{
    finalPrice: number;
    totalSaved: number;
    effectiveDiscount: number;
    firstDiscount: number;
    priceAfterFirst: number;
    secondDiscount?: number;
  } | null>(null);

  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
    success: '#10b981',
    successBg: '#f0fdf4',
    error: '#dc2626',
    errorBg: '#fef2f2',
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const originalPrice = parseFloat(inputs.originalPrice);
    const percentOff = parseFloat(inputs.percentOff);
    const additionalPercent = inputs.hasAdditional ? parseFloat(inputs.additionalPercent) || 0 : 0;

    if (isNaN(originalPrice) || originalPrice <= 0) {
      setError(t('errorInvalidPrice'));
      setResults(null);
      return;
    }

    if (isNaN(percentOff) || percentOff < 0 || percentOff > 100) {
      setError(t('errorInvalidPercent'));
      setResults(null);
      return;
    }

    if (inputs.hasAdditional && (isNaN(additionalPercent) || additionalPercent < 0 || additionalPercent > 100)) {
      setError(t('errorInvalidAdditional'));
      setResults(null);
      return;
    }

    const firstDiscount = originalPrice * (percentOff / 100);
    const priceAfterFirst = originalPrice - firstDiscount;

    let finalPrice: number;
    let totalSaved: number;
    let secondDiscount: number | undefined;

    if (inputs.hasAdditional && additionalPercent > 0) {
      secondDiscount = priceAfterFirst * (additionalPercent / 100);
      finalPrice = priceAfterFirst - secondDiscount;
      totalSaved = firstDiscount + secondDiscount;
    } else {
      finalPrice = priceAfterFirst;
      totalSaved = firstDiscount;
    }

    const effectiveDiscount = (totalSaved / originalPrice) * 100;

    setResults({
      finalPrice,
      totalSaved,
      effectiveDiscount,
      firstDiscount,
      priceAfterFirst,
      secondDiscount
    });
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const
    },
    {},
    { maxWidth: '600px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.4rem' },
    { fontSize: '1.6rem' },
    { fontSize: '1.8rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = getResponsiveStyle(
    {},
    { marginBottom: '1.25rem' },
    { marginBottom: '1.375rem' },
    { marginBottom: '1.5rem' }
  );

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: colors.textPrimary,
    fontSize: '0.95rem'
  };

  const inputWrapperStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%'
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      fontSize: '1rem',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box' as const,
      outline: 'none'
    },
    { padding: '0.75rem 1rem' },
    { padding: '0.875rem 1.25rem' },
    { padding: '1rem 1.5rem' }
  );

  const inputWithPrefixStyle = getResponsiveStyle(
    {
      ...inputStyle,
    },
    { paddingLeft: '2rem' },
    { paddingLeft: '2.25rem' },
    { paddingLeft: '2.5rem' }
  );

  const inputWithSuffixStyle = getResponsiveStyle(
    {
      ...inputStyle,
    },
    { paddingRight: '2rem' },
    { paddingRight: '2.25rem' },
    { paddingRight: '2.5rem' }
  );

  const prefixStyle: React.CSSProperties = {
    position: 'absolute',
    left: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
    pointerEvents: 'none'
  };

  const suffixStyle: React.CSSProperties = {
    position: 'absolute',
    right: '1rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#64748b',
    fontWeight: 500,
    pointerEvents: 'none'
  };

  const discountOptionsStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.5rem',
      marginBottom: '1.5rem'
    },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const discountOptionsTitleStyle: React.CSSProperties = {
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '1rem',
    fontSize: '0.95rem'
  };

  const radioGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '2rem',
    justifyContent: 'center',
    flexWrap: 'wrap'
  };

  const radioOptionStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    cursor: 'pointer'
  };

  const radioInputStyle: React.CSSProperties = {
    cursor: 'pointer',
    accentColor: '#2563eb',
    width: 'auto'
  };

  const radioLabelStyle: React.CSSProperties = {
    margin: 0,
    cursor: 'pointer',
    fontWeight: 500
  };

  const additionalDiscountStyle: React.CSSProperties = {
    display: inputs.hasAdditional ? 'block' : 'none',
    marginTop: '1rem',
    paddingTop: '1rem',
    borderTop: '2px dashed #e2e8f0'
  };

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.125rem' },
    { padding: '1.25rem 2.5rem', fontSize: '1.25rem' }
  );

  const errorMessageStyle: React.CSSProperties = {
    display: error ? 'block' : 'none',
    background: colors.errorBg,
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginTop: '1rem',
    borderLeft: '4px solid ' + colors.error
  };

  const resultsStyle = getResponsiveStyle(
    {
      display: results ? 'block' : 'none',
      background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
      borderTop: '3px solid ' + colors.success
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    {
      margin: '0 0 1.5rem 0',
      color: '#065f46',
      textAlign: 'center' as const
    },
    { fontSize: '1.25rem' },
    { fontSize: '1.35rem' },
    { fontSize: '1.4rem' }
  );

  const resultGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: screenSize === 'mobile' ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem'
  };

  const resultItemStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.5rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
    },
    { padding: '1rem' },
    { padding: '1.125rem' },
    { padding: '1.25rem' }
  );

  const resultLabelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.85rem',
    color: '#64748b',
    marginBottom: '0.5rem',
    fontWeight: 500,
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  const resultValueStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '1.5rem',
    fontWeight: 700,
    color: colors.textPrimary
  };

  const resultValueHighlightStyle: React.CSSProperties = {
    ...resultValueStyle,
    color: '#2563eb',
    fontSize: '1.8rem'
  };

  const resultValueSavingsStyle: React.CSSProperties = {
    ...resultValueStyle,
    color: colors.success
  };

  const breakdownStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '0.5rem',
      marginTop: '1rem'
    },
    { padding: '1rem' },
    { padding: '1.125rem' },
    { padding: '1.25rem' }
  );

  const breakdownTitleStyle: React.CSSProperties = {
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '1rem',
    fontSize: '1rem'
  };

  const breakdownItemStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.75rem 0',
    borderBottom: '1px solid #e2e8f0'
  };

  const breakdownItemLastStyle: React.CSSProperties = {
    ...breakdownItemStyle,
    borderBottom: 'none',
    fontWeight: 600,
    color: '#2563eb',
    paddingTop: '1rem',
    marginTop: '0.5rem',
    borderTop: '2px solid #e2e8f0'
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid ' + colors.glassBorder,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem', borderRadius: '1.25rem' },
    { padding: '2.5rem', borderRadius: '1.5rem' }
  );

  const h2Style = getResponsiveStyle(
    {
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '1rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem', marginBottom: '1.25rem' },
    { fontSize: '2rem', marginBottom: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    {
      lineHeight: 1.6,
      color: colors.textSecondary,
      marginBottom: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem', lineHeight: 1.7 },
    { fontSize: '1.25rem', lineHeight: 1.8 }
  );

  const listStyle = getResponsiveStyle(
    {
      lineHeight: 1.8,
      color: colors.textSecondary,
      paddingLeft: '1.5rem',
      marginBottom: '1rem'
    },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const listItemStyle: React.CSSProperties = {
    marginBottom: '0.75rem'
  };

  const strongStyle: React.CSSProperties = {
    color: colors.textPrimary,
    fontWeight: 600
  };

  const insightCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(255, 247, 237, 0.9) 0%, rgba(254, 243, 199, 0.9) 100%)',
      border: '1px solid rgba(251, 191, 36, 0.3)',
      borderRadius: '0.75rem',
      marginBottom: '1rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const insightTitleStyle: React.CSSProperties = {
    fontWeight: 600,
    color: '#92400e',
    marginBottom: '0.5rem',
    fontSize: '1.1rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const insightTextStyle: React.CSSProperties = {
    color: '#78350f',
    lineHeight: 1.6,
    fontSize: '0.95rem'
  };

  const faqItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '0.5rem',
      marginBottom: '1rem'
    },
    { padding: '1rem' },
    { padding: '1.25rem' },
    { padding: '1.5rem' }
  );

  const faqQuestionStyle: React.CSSProperties = {
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '0.5rem',
    fontSize: '1.05rem'
  };

  const faqAnswerStyle: React.CSSProperties = {
    color: colors.textSecondary,
    lineHeight: 1.6,
    fontSize: '0.95rem'
  };

  const referenceListStyle: React.CSSProperties = {
    listStyle: 'none',
    padding: 0,
    margin: 0
  };

  const referenceItemStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.5)',
      borderRadius: '0.5rem',
      marginBottom: '0.75rem',
      transition: 'all 0.3s ease'
    },
    { padding: '0.875rem' },
    { padding: '1rem' },
    { padding: '1.125rem' }
  );

  const referenceLinkStyle: React.CSSProperties = {
    color: colors.primary,
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: '0.95rem',
    display: 'block',
    marginBottom: '0.25rem'
  };

  const referenceUrlStyle: React.CSSProperties = {
    color: colors.textSecondary,
    fontSize: '0.85rem',
    wordBreak: 'break-all' as const
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🏷️ {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label htmlFor="original-price" style={labelStyle}>{t('originalPriceLabel')}</label>
              <div style={inputWrapperStyle}>
                <span style={prefixStyle}>$</span>
                <input
                  type="number"
                  id="original-price"
                  step="0.01"
                  min="0"
                  required
                  placeholder="39.99"
                  value={inputs.originalPrice}
                  onChange={(e) => setInputs({...inputs, originalPrice: e.target.value})}
                  style={inputWithPrefixStyle}
                />
              </div>
            </div>

            <div style={formGroupStyle}>
              <label htmlFor="percent-off" style={labelStyle}>{t('percentOffLabel')}</label>
              <div style={inputWrapperStyle}>
                <input
                  type="number"
                  id="percent-off"
                  step="0.01"
                  min="0"
                  max="100"
                  required
                  placeholder="15"
                  value={inputs.percentOff}
                  onChange={(e) => setInputs({...inputs, percentOff: e.target.value})}
                  style={inputWithSuffixStyle}
                />
                <span style={suffixStyle}>%</span>
              </div>
            </div>

            <div style={discountOptionsStyle}>
              <div style={discountOptionsTitleStyle}>{t('additionalDiscountQuestion')}</div>
              <div style={radioGroupStyle}>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="no-additional"
                    name="additional-discount"
                    checked={!inputs.hasAdditional}
                    onChange={() => setInputs({...inputs, hasAdditional: false})}
                    style={radioInputStyle}
                  />
                  <label htmlFor="no-additional" style={radioLabelStyle}>{t('no')}</label>
                </div>
                <div style={radioOptionStyle}>
                  <input
                    type="radio"
                    id="yes-additional"
                    name="additional-discount"
                    checked={inputs.hasAdditional}
                    onChange={() => setInputs({...inputs, hasAdditional: true})}
                    style={radioInputStyle}
                  />
                  <label htmlFor="yes-additional" style={radioLabelStyle}>{t('yes')}</label>
                </div>
              </div>

              <div style={additionalDiscountStyle}>
                <div style={formGroupStyle}>
                  <label htmlFor="additional-percent" style={labelStyle}>{t('additionalDiscountLabel')}</label>
                  <div style={inputWrapperStyle}>
                    <input
                      type="number"
                      id="additional-percent"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="10"
                      value={inputs.additionalPercent}
                      onChange={(e) => setInputs({...inputs, additionalPercent: e.target.value})}
                      style={inputWithSuffixStyle}
                    />
                    <span style={suffixStyle}>%</span>
                  </div>
                </div>
              </div>
            </div>

            <button type="submit" style={calculateButtonStyle}>{t('calculateButton')}</button>

            <div style={errorMessageStyle}>{error}</div>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>💰 {t('resultsTitle')}</h3>

              <div style={resultGridStyle}>
                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('finalPriceLabel')}</span>
                  <span style={resultValueHighlightStyle}>${results.finalPrice.toFixed(2)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('totalSavedLabel')}</span>
                  <span style={resultValueSavingsStyle}>${results.totalSaved.toFixed(2)}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('effectiveDiscountLabel')}</span>
                  <span style={resultValueStyle}>{results.effectiveDiscount.toFixed(2)}%</span>
                </div>
              </div>

              <div style={breakdownStyle}>
                <div style={breakdownTitleStyle}>{t('breakdownTitle')}</div>
                <div style={breakdownItemStyle}>
                  <span>{t('originalPrice')}:</span>
                  <span>${parseFloat(inputs.originalPrice).toFixed(2)}</span>
                </div>
                <div style={breakdownItemStyle}>
                  <span>{t('firstDiscount')} ({parseFloat(inputs.percentOff).toFixed(2)}%):</span>
                  <span>-${results.firstDiscount.toFixed(2)}</span>
                </div>
                <div style={breakdownItemStyle}>
                  <span>{t('priceAfterFirst')}:</span>
                  <span>${results.priceAfterFirst.toFixed(2)}</span>
                </div>
                {results.secondDiscount && (
                  <div style={breakdownItemStyle}>
                    <span>{t('additionalDiscount')} ({parseFloat(inputs.additionalPercent).toFixed(2)}%):</span>
                    <span>-${results.secondDiscount.toFixed(2)}</span>
                  </div>
                )}
                <div style={breakdownItemLastStyle}>
                  <span>{t('finalPrice')}:</span>
                  <span>${results.finalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>
          <span>📊</span>
          {t('introTitle')}
        </h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>
          <span>🎯</span>
          {t('howToUseTitle')}
        </h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>
          <span>💡</span>
          {t('insightsTitle')}
        </h2>

        <div style={insightCardStyle}>
          <div style={insightTitleStyle}>
            <span>🔢</span>
            {t('insight1Title')}
          </div>
          <p style={insightTextStyle}>{t('insight1Text')}</p>
        </div>

        <div style={insightCardStyle}>
          <div style={insightTitleStyle}>
            <span>🛍️</span>
            {t('insight2Title')}
          </div>
          <p style={insightTextStyle}>{t('insight2Text')}</p>
        </div>

        <div style={insightCardStyle}>
          <div style={insightTitleStyle}>
            <span>📈</span>
            {t('insight3Title')}
          </div>
          <p style={insightTextStyle}>{t('insight3Text')}</p>
        </div>

        <div style={insightCardStyle}>
          <div style={insightTitleStyle}>
            <span>⚠️</span>
            {t('insight4Title')}
          </div>
          <p style={insightTextStyle}>{t('insight4Text')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>
          <span>📖</span>
          {t('detailsTitle')}
        </h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph4')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>
          <span>❓</span>
          {t('faqTitle')}
        </h2>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq1Question')}</div>
          <div style={faqAnswerStyle}>{t('faq1Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq2Question')}</div>
          <div style={faqAnswerStyle}>{t('faq2Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq3Question')}</div>
          <div style={faqAnswerStyle}>{t('faq3Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq4Question')}</div>
          <div style={faqAnswerStyle}>{t('faq4Answer')}</div>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t('faq5Question')}</div>
          <div style={faqAnswerStyle}>{t('faq5Answer')}</div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>
          <span>📚</span>
          {t('referencesTitle')}
        </h2>
        <ul style={referenceListStyle}>
          <li style={referenceItemStyle}>
            <a href="https://www.omnicalculator.com/finance/percent-off" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              Percent Off Calculator - Omni Calculator
            </a>
            <span style={referenceUrlStyle}>https://www.omnicalculator.com/finance/percent-off</span>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.upflip.com/tools/discount-calculator" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              Discount Percentage Calculator - UpFlip
            </a>
            <span style={referenceUrlStyle}>https://www.upflip.com/tools/discount-calculator</span>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.calculator.net/percent-off-calculator.html" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              Percent Off Calculator - Calculator.net
            </a>
            <span style={referenceUrlStyle}>https://www.calculator.net/percent-off-calculator.html</span>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://sell.amazon.com/blog/calculate-percentage-off-prices" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              How to Calculate Percentage-Off Prices - Amazon
            </a>
            <span style={referenceUrlStyle}>https://sell.amazon.com/blog/calculate-percentage-off-prices</span>
          </li>
          <li style={referenceItemStyle}>
            <a href="https://www.gigacalculator.com/calculators/discount-calculator.php" target="_blank" rel="noopener noreferrer" style={referenceLinkStyle}>
              Discount Calculator - GIGACalculator
            </a>
            <span style={referenceUrlStyle}>https://www.gigacalculator.com/calculators/discount-calculator.php</span>
          </li>
        </ul>
      </section>
    </div>
  );
}